import {
  ArrowCounterClockwise,
  ArrowLeft,
  CheckCircle,
  Circle,
  CircleNotch,
  FileText,
  HouseLine,
  type Icon,
  MinusCircle,
  Plus,
  Trash,
  UploadSimple,
} from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Link, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type Device,
  getApiErrorMessage,
  type HouseholdDocumentInfo,
  useAdminHousehold,
  useResetOnboarding,
  useUpdateHousehold,
  useUploadHouseholdDocument,
} from "@/lib/api";

import { AdminShell } from "../admin-shell";
import { AbsorbedRoomRow } from "./absorbed-room-row";
import { CommandBand } from "./command-band";
import { ContactCard } from "./contact-card";
import { DocumentDeleteDialog } from "./document-delete-dialog";
import { HomeAssistantCard } from "./ha-card";
import { HeatingGroupsCard } from "./heating-groups-card";
import { PreconfigCard } from "./preconfig-card";
import { RoomCard } from "./room-card";
import { DeviceRow } from "./room-devices";
import { groupRooms } from "./room-groups";
import { SystemsCard } from "./systems-card";

const DOCUMENT_KINDS = ["floor_plan", "manual", "other"] as const;
const DEFAULT_DOCUMENT_KIND = "floor_plan";

const FIELD_LABEL = "text-primary text-[13px] font-semibold";

function isNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: number }).status === 404
  );
}

/** Progress-row status glyph mirroring the onboarding done/skipped/pending states. */
function ProgressIcon({ status }: { status: string }) {
  if (status === "completed") {
    return <CheckCircle className="text-success size-5 shrink-0" weight="light" />;
  }
  if (status === "skipped") {
    return <MinusCircle className="text-muted size-5 shrink-0" weight="light" />;
  }
  return <Circle className="text-faint size-5 shrink-0" weight="light" />;
}

/**
 * Small, secondary reset control for the Onboarding card header.
 *
 * Guarded by a lightweight inline confirm — the first click swaps the button
 * for a "Confirm reset?" / "Cancel" pair; confirming wipes the household's
 * progress via the mutation and resets the control afterward.
 */
function ResetOnboardingControl({ householdId }: { householdId: string }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const resetMutation = useResetOnboarding();

  const handleReset = async () => {
    setError("");
    try {
      await resetMutation.mutateAsync({ id: householdId });
      setConfirming(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "Couldn't reset onboarding."));
      setConfirming(false);
    }
  };

  if (resetMutation.isPending) {
    return (
      <span className="text-muted inline-flex items-center gap-1.5 text-[13px] font-medium">
        <CircleNotch className="size-3.5 animate-spin" weight="light" />
        Resetting…
      </span>
    );
  }

  if (confirming) {
    return (
      <span className="inline-flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => void handleReset()}
          className="text-destructive text-[13px] font-semibold"
        >
          Confirm reset?
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-muted hover:text-primary text-[13px] font-medium transition-colors"
        >
          Cancel
        </button>
      </span>
    );
  }

  if (error) {
    return (
      <button
        type="button"
        onClick={() => {
          setError("");
          setConfirming(true);
        }}
        title={error}
        className="text-destructive inline-flex items-center gap-1.5 text-[13px] font-medium"
      >
        <ArrowCounterClockwise className="size-3.5" weight="light" />
        Reset failed — retry
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="text-muted hover:text-primary inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors"
    >
      <ArrowCounterClockwise className="size-3.5" weight="light" />
      Reset onboarding
    </button>
  );
}

/** Section header shared by the main-column cards. */
function CardHeader({
  icon: Icon,
  title,
  children,
}: {
  icon: Icon;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="border-border-subtle flex items-center justify-between gap-3 border-b px-5 py-[18px]">
      <h2 className="text-primary flex items-center gap-2.5 text-[17px] font-bold">
        <Icon className="text-muted size-[18px]" weight="light" />
        {title}
      </h2>
      {children}
    </div>
  );
}

export function AdminHouseholdDetailPage() {
  const { id } = useParams<{ id: string }>();
  const householdQuery = useAdminHousehold(id ?? null);
  const uploadMutation = useUploadHouseholdDocument();
  const roomsMutation = useUpdateHousehold();
  const houseDevicesMutation = useUpdateHousehold();

  const household = householdQuery.data;

  // Upload-form state.
  const [docTitle, setDocTitle] = useState("");
  const [docKind, setDocKind] = useState<string>(DEFAULT_DOCUMENT_KIND);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [deleteDoc, setDeleteDoc] = useState<HouseholdDocumentInfo | null>(null);

  const handleAddRoom = () => {
    if (!household) {
      return;
    }
    void roomsMutation.mutateAsync({
      id: household.id,
      body: {
        rooms: [
          ...household.rooms,
          { name: `Room ${household.rooms.length + 1}`, windows: [], devices: [] },
        ],
      },
    });
  };

  const handleHouseDeviceChange = async (deviceIndex: number, patch: Partial<Device>) => {
    if (!household) {
      return;
    }
    const nextDevices = household.house_devices.map((d, i) =>
      i === deviceIndex ? { ...d, ...patch } : d,
    );
    await houseDevicesMutation.mutateAsync({
      id: household.id,
      body: { house_devices: nextDevices },
    });
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !docFile) {
      return;
    }
    setUploadError("");
    try {
      await uploadMutation.mutateAsync({
        householdId: id,
        file: docFile,
        title: docTitle,
        kind: docKind,
      });
      setDocTitle("");
      setDocKind(DEFAULT_DOCUMENT_KIND);
      setDocFile(null);
    } catch (err) {
      setUploadError(getApiErrorMessage(err, "Failed to upload document."));
    }
  };

  return (
    <AdminShell
      title={household ? household.name : "Household"}
      actions={
        <Link
          to="/admin/households"
          className="text-muted hover:text-primary inline-flex items-center gap-1.5 text-[15px] font-medium transition-colors"
        >
          <ArrowLeft className="size-4" weight="light" />
          All households
        </Link>
      }
    >
      {householdQuery.isLoading ? (
        <div className="bg-card shadow-elevation flex justify-center rounded-md py-16">
          <CircleNotch className="text-muted h-6 w-6 animate-spin" weight="light" />
        </div>
      ) : isNotFound(householdQuery.error) ? (
        <div className="bg-card shadow-elevation rounded-md py-12 text-center">
          <p className="text-muted text-[15px]">
            Household not found.{" "}
            <Link to="/admin/households" className="text-accent underline">
              Back to list
            </Link>
            .
          </p>
        </div>
      ) : householdQuery.isError || !household ? (
        <div className="bg-card shadow-elevation rounded-md py-12 text-center">
          <p className="text-muted text-[15px]">
            Could not load household. Please{" "}
            <button
              type="button"
              onClick={() => void householdQuery.refetch()}
              className="text-accent underline"
            >
              try again
            </button>
            .
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          <CommandBand household={household} />

          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
            {/* MAIN */}
            <div className="flex flex-col gap-5">
              <SystemsCard household={household} />
              <PreconfigCard household={household} />
              <HeatingGroupsCard householdId={household.id} />

              {/* Rooms & hardware */}
              <div className="bg-card shadow-elevation rounded-md">
                <CardHeader icon={HouseLine} title="Rooms & hardware">
                  <div className="flex items-center gap-4">
                    <div className="text-muted hidden items-center gap-3.5 text-xs sm:flex">
                      <span className="flex items-center gap-1.5">
                        <span className="bg-accent size-2 rounded-full" />
                        Admin seed
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="bg-border-strong size-2 rounded-full" />
                        Captured in setup
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddRoom}
                      disabled={roomsMutation.isPending}
                      className="text-primary focus-visible:outline-ring inline-flex items-center gap-1 text-[13px] font-semibold hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                    >
                      <Plus className="size-3.5" weight="light" />
                      Add room
                    </button>
                  </div>
                </CardHeader>
                <div className="flex flex-col gap-3 p-4">
                  {household.rooms.length === 0 ? (
                    <p className="text-muted px-1 py-2 text-[15px]">
                      No rooms yet — add the first one above.
                    </p>
                  ) : (
                    groupRooms(household.rooms).map((group, g) => {
                      // Indexed access is `Room | undefined` under strict checks; the
                      // grouping only ever yields in-range indices, so the guards are
                      // for the type, not for a case that can happen.
                      const survivorName = household.rooms[group.index]?.name || "its survivor";
                      return (
                        <div key={group.index} className="flex flex-col gap-1.5">
                          <RoomCard
                            household={household}
                            roomIndex={group.index}
                            defaultOpen={g === 0}
                          />
                          {group.absorbed.map((i) => {
                            const absorbed = household.rooms[i];
                            return absorbed ? (
                              <AbsorbedRoomRow
                                key={i}
                                room={absorbed}
                                survivorName={survivorName}
                              />
                            ) : null;
                          })}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Whole-house devices */}
              <div className="bg-card shadow-elevation rounded-md">
                <CardHeader icon={HouseLine} title="Whole-house devices">
                  <span className="text-muted text-[13px]">Serve every room</span>
                </CardHeader>
                <div className="px-5 pt-1 pb-3.5">
                  {household.house_devices.length === 0 ? (
                    <p className="text-muted py-3 text-[15px]">No whole-house devices yet.</p>
                  ) : (
                    household.house_devices.map((device, i) => (
                      <DeviceRow
                        key={i}
                        device={device}
                        onChange={(patch) => handleHouseDeviceChange(i, patch)}
                        bleed
                        airthingsSensorIds={
                          household.preconfig.airthings?.map((s) => s.sensor_id) ?? []
                        }
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Documents */}
              <div className="bg-card shadow-elevation rounded-md">
                <CardHeader icon={FileText} title="Documents" />
                <div className="p-5">
                  {household.documents.length === 0 ? (
                    <p className="text-muted text-[15px]">No documents yet.</p>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {household.documents.map((doc) => (
                        <li
                          key={doc.id}
                          className="bg-background flex items-center justify-between rounded-xl px-4 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <span className="bg-surface-subtle text-muted inline-flex rounded-sm px-2.5 py-1 text-[13px] font-semibold">
                              {doc.kind}
                            </span>
                            {doc.url ? (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary text-[15px] font-medium underline"
                              >
                                {doc.title}
                              </a>
                            ) : (
                              <span className="text-primary text-[15px] font-medium">
                                {doc.title}
                              </span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${doc.title}`}
                            onClick={() => setDeleteDoc(doc)}
                          >
                            <Trash className="text-destructive h-4 w-4" weight="light" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <form
                    onSubmit={handleUpload}
                    className="border-border mt-6 flex flex-col gap-4 border-t pt-6"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <label htmlFor="doc-title" className={FIELD_LABEL}>
                          Title
                        </label>
                        <Input
                          id="doc-title"
                          value={docTitle}
                          onChange={(e) => setDocTitle(e.target.value)}
                          required
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label htmlFor="doc-kind" className={FIELD_LABEL}>
                          Kind
                        </label>
                        <select
                          id="doc-kind"
                          value={docKind}
                          onChange={(e) => setDocKind(e.target.value)}
                          className="border-input bg-card text-foreground focus-visible:ring-ring h-10 w-full rounded-xl border px-3 text-[15px] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                        >
                          {DOCUMENT_KINDS.map((kind) => (
                            <option key={kind} value={kind}>
                              {kind}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="doc-file" className={FIELD_LABEL}>
                        File
                      </label>
                      <input
                        id="doc-file"
                        type="file"
                        onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
                        required
                        className="text-muted file:border-input file:bg-card file:text-primary w-full text-[15px] file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-[13px] file:font-medium"
                      />
                    </div>
                    {uploadError && (
                      <p role="alert" className="text-destructive text-[15px]">
                        {uploadError}
                      </p>
                    )}
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={uploadMutation.isPending || !docFile}
                        className="px-5"
                      >
                        {uploadMutation.isPending ? (
                          <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
                        ) : (
                          <>
                            <UploadSimple className="mr-2 h-4 w-4" weight="light" />
                            Upload
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* RAIL */}
            <div className="flex flex-col gap-5 lg:sticky lg:top-[84px]">
              <HomeAssistantCard household={household} />
              <ContactCard household={household} />

              <div className="bg-card shadow-elevation rounded-md">
                <CardHeader icon={CheckCircle} title="Onboarding">
                  <div className="flex items-center gap-3.5">
                    <ResetOnboardingControl householdId={household.id} />
                    <span className="text-muted text-[13px]">
                      {household.progress.filter((s) => s.status === "completed").length} /{" "}
                      {household.progress.length}
                    </span>
                  </div>
                </CardHeader>
                <ul className="flex flex-col gap-1 px-5 py-3">
                  {household.progress.map((step) => (
                    <li
                      key={step.step_key}
                      className="flex items-center justify-between gap-3 py-1.5"
                    >
                      <span className="flex items-center gap-2.5">
                        <ProgressIcon status={step.status} />
                        <span className="text-primary text-[13.5px]">{step.title}</span>
                      </span>
                      {step.completed_at && (
                        <span className="text-muted font-mono text-[11.5px] whitespace-nowrap">
                          {new Date(step.completed_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <DocumentDeleteDialog
            householdId={household.id}
            document={deleteDoc}
            open={!!deleteDoc}
            onOpenChange={(open) => !open && setDeleteDoc(null)}
          />
        </div>
      )}
    </AdminShell>
  );
}
